import "../reports.css";
import {
  buildFilterObject,
  Edit,
  EditSelectionItemProps,
  FilterProps,
  StringMap,
} from "../../../components";
import { useLanguage } from "../../../hooks";
import { Select } from "antd";
import {
  OWN_SALES_PRODUCT_FILTER,
  productOptionLabel,
  ReportSalesFilterOptionsProps,
  ReportsTabEnum,
  ReportTabRow,
  SALES_CATEGORY_FILTER,
  SALES_FI_TERMINAL_SELECT_FILTER,
} from "../reports.props";

// Opciones de un desplegable a partir de los valores distintos de un campo en las
// filas cargadas en pantalla, ordenadas alfabéticamente.
const toItems = (values: string[]): EditSelectionItemProps[] =>
  values.map((value) => ({ key: value, text: value }));

const distinctItems = (
  rows: ReportTabRow[],
  field: string
): EditSelectionItemProps[] =>
  Array.from(
    new Set(
      rows
        .map((row) => (row as unknown as Record<string, unknown>)[field])
        .filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0
        )
    )
  )
    .sort((a, b) => a.localeCompare(b, "es", { numeric: true }))
    .map((value) => ({ key: value, text: value }));

enum ReportFilterEnum {
  BRAND_NAME = "brandName",
  // Estado tarjetas: caducidad hasta esa fecha (DD/MM/YYYY), ver ReportStatusCards.
  EXPIRES_BEFORE = "expiresBefore",
  FI_TERMINAL = "fiTerminal",

  RETAILER = "retailer",
  ESTABLISHMENT = "establishment",
  OPERATION = "operation",
}

interface ReportFiltersProps {
  type: ReportsTabEnum;
  filters: StringMap;
  setFilters: React.Dispatch<React.SetStateAction<StringMap>>;
  // Informes de ventas: opciones sobre todo el histórico (los filtros se aplican en
  // el servidor, el informe está paginado).
  salesOptions?: ReportSalesFilterOptionsProps;
  // Estado tarjetas: filas cargadas, de donde salen las marcas del desplegable.
  rows: ReportTabRow[];
}

// Filtros por pestaña:
// - Estado tarjetas: marca y caducidad (caduca antes de una fecha).
// - Ventas propias: categoría, producto, FiTerminal (con buscador), retailer y operación.
// - Ventas mis productos: producto, FiTerminal (texto), retailer, punto de venta y operación.
const ReportFilters: React.FC<ReportFiltersProps> = ({
  type,
  filters,
  setFilters,
  salesOptions,
  rows,
}) => {
  const { t } = useLanguage();

  const isStatusCardTab = type === ReportsTabEnum.ReportsStatusCard;
  const isOwnSalesTab = type === ReportsTabEnum.ReportsOwnSales;
  const isMyProductsTab = type === ReportsTabEnum.ReportsSalesMyProducts;
  const isSalesTab = isOwnSalesTab || isMyProductsTab;

  const itemsOperation: EditSelectionItemProps[] = [
    { key: "activation", text: t.reportsScreen.table.tbody.activation },
    { key: "refund", text: t.reportsScreen.table.tbody.refund },
    { key: "cancellation", text: t.reportsScreen.table.tbody.cancellation },
  ];
  const itemsCategory = toItems(salesOptions?.categories ?? []);
  const itemsProduct: EditSelectionItemProps[] = (
    salesOptions?.products ?? []
  ).map((product) => ({
    key: product.idProd,
    text: productOptionLabel(product),
  }));
  const itemsCardBrand = distinctItems(rows, ReportFilterEnum.BRAND_NAME);
  const itemsRetailer = toItems(salesOptions?.retailer ?? []);
  const itemsEstablishment = toItems(salesOptions?.establishment ?? []);

  const handleChange = (data: FilterProps) => {
    const { key, value } = data;
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  const handleSelection = (id: string, data: EditSelectionItemProps) => {
    handleChange({ key: id, value: data.key });
  };

  const selection = (
    id: string,
    show: boolean,
    placeholder: string,
    items: EditSelectionItemProps[]
  ) => (
    <Edit.Selection
      id={id}
      show={show}
      placeholder={placeholder}
      items={items}
      selected={buildFilterObject(id, filters, items)}
      onSelect={(data, selectedId) => handleSelection(selectedId, data)}
    />
  );

  return (
    <Edit>
      {/* MARCA (Estado tarjetas: de las tarjetas en pantalla) */}
      {selection(
        ReportFilterEnum.BRAND_NAME,
        isStatusCardTab,
        t.reportsScreen.filters.brand,
        itemsCardBrand
      )}

      {/* CADUCA ANTES DE (Estado tarjetas) */}
      <Edit.CalendarSelect
        id={ReportFilterEnum.EXPIRES_BEFORE}
        show={isStatusCardTab}
        placeholder={t.reportsScreen.filters.expiresBefore}
        value={filters[ReportFilterEnum.EXPIRES_BEFORE]}
        onChange={(value) =>
          handleChange({ key: ReportFilterEnum.EXPIRES_BEFORE, value })
        }
      />

      {/* CATEGORÍA (ventas propias: para un canal las marcas no aportan) */}
      {selection(
        SALES_CATEGORY_FILTER,
        isOwnSalesTab,
        t.reportsScreen.filters.category,
        itemsCategory
      )}

      {/* PRODUCTO (código prod + nombre, filtrado en servidor) */}
      {selection(
        OWN_SALES_PRODUCT_FILTER,
        isSalesTab,
        t.reportsScreen.filters.productSelect,
        itemsProduct
      )}

      {/* FITERMINAL: desplegable en ventas propias, texto en ventas mis productos */}
      {/* Un canal puede tener miles de FiTerminales: desplegable con buscador y lista virtual. */}
      {isOwnSalesTab && (
        <div className="edit-box-wrapper report-search-select">
          <Select
            showSearch
            allowClear
            className="edit-box"
            classNames={{ popup: { root: "edit-selection-overlay" } }}
            placeholder={t.reportsScreen.filters.fiTerminalSelect}
            value={filters[SALES_FI_TERMINAL_SELECT_FILTER] || undefined}
            options={(salesOptions?.fiTerminal ?? []).map((value) => ({
              value,
              label: value,
            }))}
            onChange={(value) =>
              handleChange({
                key: SALES_FI_TERMINAL_SELECT_FILTER,
                value: value ?? "",
              })
            }
          />
        </div>
      )}
      <Edit.Input
        id={ReportFilterEnum.FI_TERMINAL}
        show={isMyProductsTab}
        placeholder={t.reportsScreen.filters.fiTerminal}
        value={filters[ReportFilterEnum.FI_TERMINAL]}
        onChange={(e) =>
          handleChange({ key: e.target.id, value: e.target.value })
        }
      />

      {/* RETAILER */}
      {selection(
        ReportFilterEnum.RETAILER,
        isSalesTab,
        t.reportsScreen.filters.retailerSelect,
        itemsRetailer
      )}

      {/* PUNTO DE VENTA (ventas mis productos) */}
      {selection(
        ReportFilterEnum.ESTABLISHMENT,
        isMyProductsTab,
        t.reportsScreen.filters.establishmentSelect,
        itemsEstablishment
      )}

      {/* OPERACIÓN */}
      {selection(
        ReportFilterEnum.OPERATION,
        isSalesTab,
        t.reportsScreen.filters.operation,
        itemsOperation
      )}
    </Edit>
  );
};

export default ReportFilters;
